// Builds the list of pages the spotlight can navigate to.
//
// Two sources, merged:
//
//  1. The router itself — every static (non-parameterised) route, access-checked
//     with the same `getRouteAccessDecision` the navigation guard uses. This is
//     what makes deep pages discoverable: /settings/discount-presets and
//     /settings/domains never appear in any sidebar, but they are exactly the
//     kind of thing people hunt for.
//  2. The role sidebar — supplies the curated human label, icon and section for
//     the paths it covers ("Payroll Insights", not "PayrollMonthlyAttendance"),
//     plus the handful of entries that carry a query string.
//
// The result is cached per (roles, permissions, organisation, user) and rebuilt
// only when one of those changes, so opening the palette costs nothing.

import { getRouteAccessDecision, getStoredRoles } from '@/auth/access'
import { getSidebarForRoles, withUserNavExtras } from '@/config/roleSidebarMap'
import { normalizeNavIcon } from '@/config/navIcons'

// Route subtrees that exist because this app started life as a CoreUI admin
// template, plus the auth/error shells. None of them are real OMS destinations.
const EXCLUDED_PREFIXES = [
  '/base',
  '/bread',
  '/buttons',
  '/charts',
  '/colors',
  '/email',
  '/icon',
  '/login',
  '/media',
  '/menu',
  '/menuelement',
  '/notes',
  '/notifications',
  '/pages',
  '/register',
  '/resource',
  '/roles',
  '/typography',
  '/users',
  '/widgets',
]

// Path prefix → section shown next to the result, for pages the sidebar
// doesn't cover. Longest prefix wins.
const SECTION_BY_PREFIX = [
  ['/settings', 'Settings'],
  ['/staff', 'Staff'],
  ['/websites', 'E-Commerce'],
  ['/attendance', 'Attendance'],
  ['/shipping', 'Operations'],
  ['/info-center', 'Resources'],
  ['/vault', 'Resources'],
]

// Default icons for router-only pages, again by longest matching prefix.
const ICON_BY_PREFIX = [
  ['/settings', 'cilSettings'],
  ['/staff', 'cilPeople'],
  ['/websites', 'cilCart'],
  ['/attendance', 'cilClock'],
  ['/shipping', 'cilTruck'],
  ['/info-center', 'cilLibrary'],
  ['/vault', 'cilLockLocked'],
]

// Search synonyms. People type what they call the thing, not what the page is
// titled — "cod" for COD Payments, "gst" for the Tally export, "staff" for
// Users. Terms are matched as ordinary haystack text.
const KEYWORDS = {
  '/dashboard': 'home overview kpi metrics',
  '/analytics': 'insights trends graphs charts',
  '/inbox': 'notifications messages alerts approvals',
  '/orders': 'order search invoice tracking awb shipment',
  '/orderform': 'new order create order booking',
  '/telesales': 'tele sales lead entry call form new',
  '/pos': 'point of sale billing counter shop cash',
  '/pos-sales': 'counter sales shop report',
  '/mvpbulk': 'bulk mvp multiple orders',
  '/mapbulk': 'bulk map multiple orders',
  '/fieldsalesform': 'field staff visit entry new',
  '/fieldvisits': 'field staff visits log',
  '/customers': 'crm contacts patients buyers',
  '/telesales-customers': 'crm explorer contacts patients search',
  '/call_log': 'calls history telecalling records',
  '/callqueue': 'calls dialer queue pending',
  '/callsummary': 'calls summary agent performance',
  '/grade_followups': 'followups grading callbacks',
  '/leaderboard': 'ranking top agents contest',
  '/hms_imports': 'incentive hms upload import',
  '/incentive_preview': 'incentives commission payout preview',
  '/tele_sales_accounts': 'incentive accounts telesales wallet',
  '/pendingpayments': 'cod collections vpp pending payment postal',
  '/razorpay-transactions': 'payment gateway online transactions upi',
  '/refund': 'refunds money back returns credit',
  '/tallyexport': 'accounting export tally erp gst ledger',
  '/zohoexport': 'accounting export zoho books invoices',
  '/Verifyzohoinvoices': 'zoho verify invoices reconciliation',
  '/salesreport': 'reports revenue sales figures',
  '/branchreport': 'reports branch wise location',
  '/departmentreport': 'reports department wise team',
  '/agentwise_report': 'reports agent wise staff performance',
  '/telesales_report': 'reports telesales calling performance',
  '/reports': 'download export csv excel reports',
  '/shipping/dashboard': 'courier delivery tracking dispatch logistics',
  '/shipping/scanner': 'scan barcode ready to ship dispatch pack',
  '/shipping/returns': 'rto returns undelivered mark',
  '/packingBatches': 'packing batch pack dispatch',
  '/barcodes': 'article numbers courier tracking stickers',
  '/websites': 'ecommerce shop online store site',
  '/attendance/monthly': 'payroll salary overtime insights monthly',
  '/attendance/hours': 'attendance hours worked biometric punch',
  '/attendance/my': 'my attendance punches biometric self',
  '/attendance/devices': 'biometric machine essl device',
  '/attendance/pins': 'biometric pin mapping staff enrol',
  '/info-center': 'docs documentation knowledge base help sop',
  '/private-notes': 'personal notes scratchpad',
  '/vault': 'passwords credentials secrets logins',
  '/settings': 'configuration admin preferences setup',
  '/settings/users': 'staff employees accounts team members',
  '/settings/roles': 'permissions access control',
  '/settings/products': 'catalogue items skus medicines',
  '/settings/pincodes': 'postal codes post office address lookup',
  '/settings/shipping': 'cod fee charges delivery',
  '/settings/discount-presets': 'discounts offers tiers pricing',
  '/settings/domains': 'dns namecheap website domain records',
  '/settings/invoice': 'prefix numbering counter bill series',
  '/settings/organisations': 'company entity org',
  '/settings/departments': 'teams division',
  '/settings/branches': 'locations offices',
  '/settings/quick-replies': 'canned responses templates whatsapp',
  '/settings/call-queues': 'calls routing queue configuration',
  '/settings/dropdownitems': 'options dictionary lists',
  '/staff/users': 'employees hr team members',
  '/profile-settings': 'my profile account password personal',
  '/tickets': 'support complaints issues',
  '/alltickets': 'support complaints issues all',
  '/calendar': 'schedule events dates',
  '/network-speed': 'connection diagnostics latency',
}

let cache = { key: null, entries: [], byPath: new Map() }

function joinPath(base, segment) {
  if (!segment) {
    return base || '/'
  }
  if (segment.startsWith('/')) {
    return segment
  }
  return `${base === '/' ? '' : base}/${segment}`
}

function isExcluded(path) {
  return EXCLUDED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))
}

function longestPrefixMatch(pairs, path, fallback) {
  let best = fallback
  let bestLength = -1

  pairs.forEach(([prefix, value]) => {
    if ((path === prefix || path.startsWith(`${prefix}/`)) && prefix.length > bestLength) {
      best = value
      bestLength = prefix.length
    }
  })

  return best
}

// 'PayrollMonthlyAttendance' → 'Payroll Monthly Attendance'
function humanizeName(name) {
  if (!name) {
    return ''
  }
  return String(name)
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Flatten the router config into `{ path, name, meta }` for every static,
 * component-backed route.
 */
function collectRoutes(routes, base, out) {
  if (!Array.isArray(routes)) {
    return out
  }

  routes.forEach((route) => {
    if (!route || typeof route.path !== 'string') {
      return
    }

    const path = joinPath(base, route.path)
    const isStatic = !path.includes(':') && !path.includes('*')

    // A `beforeEnter` guard encodes a condition this catalog can't evaluate
    // (e.g. the single-user Karnataka page). Skip it here — if the user is
    // meant to see it, the sidebar injects it and enrichment adds it back.
    const isNavigable = isStatic
      && route.component
      && !route.redirect
      && !route.beforeEnter
      && !isExcluded(path)

    if (isNavigable) {
      out.push({ path, name: route.name || '', meta: route.meta || {} })
    }

    if (route.children) {
      collectRoutes(route.children, path, out)
    }
  })

  return out
}

/**
 * Walk the sidebar tree into flat `{ href, name, icon, section, group }`
 * records, applying the same organisation / permission filter the sidebar
 * itself applies so a hidden nav item never leaks into search results.
 */
function collectSidebar(items, context, section, group, out) {
  if (!Array.isArray(items)) {
    return out
  }

  items.forEach((item) => {
    if (!item || !isSidebarItemAllowed(item, context)) {
      return
    }

    if (item.slug === 'title') {
      context.section = item.name || ''
      return
    }

    const currentSection = section || context.section

    if (item.slug === 'dropdown') {
      collectSidebar(item.elements, context, currentSection, item.name || '', out)
      return
    }

    if (item.slug === 'link' && typeof item.href === 'string' && item.href.startsWith('/')) {
      out.push({
        href: item.href,
        name: item.name || '',
        icon: item.icon || '',
        // An explicit `section` wins: items appended after the last `title`
        // entry (the per-user extras) would otherwise inherit it.
        section: item.section || currentSection,
        group,
      })
    }
  })

  return out
}

function isSidebarItemAllowed(item, context) {
  if (Array.isArray(item.organisationIds) && item.organisationIds.length) {
    if (!item.organisationIds.map(Number).includes(Number(context.activeOrganisationId || 0))) {
      return false
    }
  }

  if (item.permission && !context.can(item.permission)) {
    return false
  }

  return true
}

function isReachable(router, href) {
  try {
    const resolved = router.resolve(href)
    const route = resolved && resolved.route

    if (!route || !route.matched || !route.matched.length) {
      return false
    }

    // The `*` catch-all matches anything, so a stale href would otherwise look
    // reachable and then land the user on the 404 page.
    if (route.name === '404') {
      return false
    }

    return getRouteAccessDecision(route).allowed
  } catch (error) {
    return false
  }
}

// Precompute everything the matcher needs so a keystroke only does indexOf
// calls over strings that already exist.
function decorate(entry, order) {
  const title = entry.title
  const lowerTitle = title.toLowerCase()
  const initials = lowerTitle
    .split(/[\s/&·-]+/)
    .filter(Boolean)
    .map((word) => word[0])
    .join('')

  const haystack = [
    lowerTitle,
    (entry.section || '').toLowerCase(),
    (entry.group || '').toLowerCase(),
    entry.path.toLowerCase().replace(/[/_-]+/g, ' ').trim(),
    (entry.keywords || '').toLowerCase(),
  ].filter(Boolean).join(' ')

  // Frozen so Vue 2 skips it when the results array lands in a reactive data
  // property — observing a few hundred entries on every open would cost more
  // than the search itself.
  return Object.freeze({
    ...entry,
    order,
    lowerTitle,
    initials,
    haystack,
  })
}

function cacheKey(context) {
  return [
    context.roles,
    context.userId,
    context.activeOrganisationId,
    context.permissionsKey,
  ].join('|')
}

/**
 * Build (or return the cached) list of navigable pages for the current user.
 *
 * @param {object} options
 * @param {object} options.router - The vue-router instance
 * @param {number|null} options.activeOrganisationId
 * @param {object} options.permissions - Vuex permission map
 * @param {function} options.can - Permission check, i.e. the `can` getter
 * @param {number|null} options.userId
 * @returns {{ entries: array, byPath: Map }}
 */
export function buildCatalog({ router, activeOrganisationId, permissions, can, userId }) {
  const context = {
    roles: getStoredRoles().join(','),
    userId: userId == null ? '' : String(userId),
    activeOrganisationId: activeOrganisationId || '',
    permissionsKey: Object.keys(permissions || {}).sort().join(','),
    can: typeof can === 'function' ? can : () => true,
    section: '',
  }

  const key = cacheKey(context)
  if (cache.key === key) {
    return cache
  }

  const sidebar = withUserNavExtras(getSidebarForRoles(context.roles), userId)
  const sidebarLinks = collectSidebar(sidebar, context, '', '', [])

  // href (with query) → sidebar record, so /attendance/hours and
  // /attendance/hours?device=… stay distinct entries.
  const sidebarByHref = new Map()
  sidebarLinks.forEach((link) => {
    if (!sidebarByHref.has(link.href)) {
      sidebarByHref.set(link.href, link)
    }
  })

  const entries = []
  const seen = new Set()

  const push = (entry) => {
    if (seen.has(entry.id)) {
      return
    }
    seen.add(entry.id)
    entries.push(decorate(entry, entries.length))
  }

  // Sidebar first: its order is the curated one, which doubles as the default
  // ranking for the "Suggested" group before any usage history exists.
  sidebarLinks.forEach((link) => {
    if (!isReachable(router, link.href)) {
      return
    }

    const path = link.href.split('?')[0]
    push({
      id: link.href,
      path,
      target: link.href,
      title: link.name || humanizeName(path.split('/').pop()),
      section: link.section || longestPrefixMatch(SECTION_BY_PREFIX, path, 'Pages'),
      group: link.group || '',
      icon: normalizeNavIcon(link.icon || longestPrefixMatch(ICON_BY_PREFIX, path, 'cilList')),
      keywords: KEYWORDS[path] || '',
      inSidebar: true,
    })
  })

  // Then everything else the router exposes and the guard would let through.
  collectRoutes(router.options.routes, '', []).forEach((route) => {
    if (sidebarByHref.has(route.path) || seen.has(route.path)) {
      return
    }

    if (!isReachable(router, route.path)) {
      return
    }

    const title = route.meta.label || humanizeName(route.name) || humanizeName(route.path.split('/').pop())
    if (!title) {
      return
    }

    push({
      id: route.path,
      path: route.path,
      target: route.path,
      title,
      section: longestPrefixMatch(SECTION_BY_PREFIX, route.path, 'Pages'),
      group: '',
      icon: normalizeNavIcon(longestPrefixMatch(ICON_BY_PREFIX, route.path, 'cilList')),
      keywords: [KEYWORDS[route.path] || '', route.meta.description || ''].filter(Boolean).join(' '),
      inSidebar: false,
    })
  })

  const byPath = new Map()
  entries.forEach((entry) => {
    if (!byPath.has(entry.path)) {
      byPath.set(entry.path, entry)
    }
  })

  cache = { key, entries, byPath }
  return cache
}

export function invalidateCatalog() {
  cache = { key: null, entries: [], byPath: new Map() }
}
